import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { Users, Search, Plus, Filter, Mail, Phone, Tag, ChevronRight, UserCheck } from "lucide-react";
import UnifiedTimeline from "@/components/UnifiedTimeline";
import { toast } from "sonner";

export default function ContactsPage() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newNote, setNewNote] = useState("");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    roles: ["Donor"],
    tags: "",
  });

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ["contacts", searchTerm, selectedRole],
    queryFn: async () => {
      const res = await apiClient.get("/contacts", {
        params: { search: searchTerm, role: selectedRole },
      });
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post("/contacts", {
        ...data,
        tags: data.tags ? data.tags.split(",").map((t) => t.trim()) : [],
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["contacts"]);
      setIsCreateOpen(false);
      setFormData({ first_name: "", last_name: "", email: "", phone: "", roles: ["Donor"], tags: "" });
      toast.success("Contact profile created successfully!");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to create contact");
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: async ({ contactId, title }) => {
      const res = await apiClient.post(`/contacts/${contactId}/notes`, { title });
      return res.data;
    },
    onSuccess: (updatedContact) => {
      queryClient.invalidateQueries(["contacts"]);
      setSelectedContact(updatedContact);
      setNewNote("");
      toast.success("Note added to timeline");
    },
  });

  const handleRoleToggle = (role) => {
    setFormData((prev) => {
      const exists = prev.roles.includes(role);
      const roles = exists ? prev.roles.filter((r) => r !== role) : [...prev.roles, role];
      return { ...prev, roles: roles.length ? roles : ["Donor"] };
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-indigo-600" />
            Unified CRM & Contact Profiles
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Consolidated directory with multi-role identity tracking and aggregated interaction history.
          </p>
        </div>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm rounded-lg shadow-sm transition-all"
        >
          <Plus className="w-4 h-4" />
          Add New Contact
        </button>
      </div>

      {/* Filters & Search */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name or email address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
          >
            <option value="">All Identity Roles</option>
            <option value="Donor">Donors</option>
            <option value="Volunteer">Volunteers</option>
            <option value="Beneficiary">Beneficiaries</option>
            <option value="Board Member">Board Members</option>
          </select>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading contacts...</div>
          ) : contacts.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500 space-y-2">
              <UserCheck className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="font-medium text-slate-700">No contact profiles found</p>
              <p className="text-xs text-slate-400">Click "Add New Contact" to record a profile.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {contacts.map((contact) => (
                <div
                  key={contact._id}
                  onClick={() => setSelectedContact(contact)}
                  className={`p-4 flex items-center justify-between hover:bg-slate-50 cursor-pointer transition-colors ${
                    selectedContact?._id === contact._id ? "bg-indigo-50/50 border-l-4 border-indigo-600" : ""
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 text-sm">
                        {contact.first_name} {contact.last_name}
                      </h3>
                      <div className="flex items-center gap-1">
                        {contact.roles?.map((r) => (
                          <span
                            key={r}
                            className="px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-700 rounded-full"
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        {contact.email}
                      </span>
                      {contact.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5 text-slate-400" />
                          {contact.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Contact Profile Drawer / Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-6">
          {selectedContact ? (
            <>
              <div>
                <span className="text-xs font-semibold uppercase text-indigo-600 tracking-wider">
                  Contact Timeline & Details
                </span>
                <h2 className="text-xl font-bold text-slate-900 mt-1">
                  {selectedContact.first_name} {selectedContact.last_name}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">{selectedContact.email}</p>
              </div>

              {/* Roles badge */}
              <div className="space-y-2">
                <span className="text-xs font-medium text-slate-500">Assigned Roles</span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedContact.roles?.map((r) => (
                    <span
                      key={r}
                      className="px-2.5 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-100"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quick Note Input */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <label className="text-xs font-medium text-slate-700">Log Quick Interaction Note</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter interaction note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <button
                    disabled={!newNote.trim()}
                    onClick={() =>
                      addNoteMutation.mutate({ contactId: selectedContact._id, title: newNote })
                    }
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg"
                  >
                    Log
                  </button>
                </div>
              </div>

              {/* Aggregated Timeline */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <h3 className="text-xs font-semibold uppercase text-slate-400 tracking-wider">
                  Aggregated Activity Timeline
                </h3>
                <UnifiedTimeline events={selectedContact.timeline_events || []} />
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-sm text-slate-400">
              Select a contact profile to view details & unified timeline.
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-bold text-slate-900">Create Unified Contact Profile</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(formData);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700">First Name</label>
                  <input
                    required
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Last Name</label>
                  <input
                    required
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700">Email Address</label>
                <input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700">Phone Number</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Assigned Identity Roles</label>
                <div className="flex flex-wrap gap-2">
                  {["Donor", "Volunteer", "Beneficiary", "Board Member"].map((role) => (
                    <button
                      type="button"
                      key={role}
                      onClick={() => handleRoleToggle(role)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                        formData.roles.includes(role)
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-slate-50 text-slate-600 border-slate-200"
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsCreateOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                >
                  {createMutation.isPending ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
